import { AnalyticObject } from './../interfaces/analytics-interfaces';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ChatMessageErrorEvent } from './chat-conversation.service';

export type ChatLogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

const LOG_LEVEL_RANK: Record<ChatLogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3, none: 4 };

export class ChatLogEvent {
  logMessage: string;
  logParameters?: any[];
  // Set by ChatLoggingService when the event is emitted
  level?: Exclude<ChatLogLevel, 'none'>;
}

@Injectable({
  providedIn: 'root'
})
export class ChatLoggingService {
  public recentErrors: ChatLogEvent[] = [];
  // Emits debug, info and warn events (check event.level)
  public infoSubject$?: Subject<ChatLogEvent> = new Subject<ChatLogEvent>();
  public errorSubject$?: Subject<ChatLogEvent> = new Subject<ChatLogEvent>();
  public chatMessageErrorSubject$?: Subject<ChatMessageErrorEvent> = new Subject<ChatMessageErrorEvent>();
  public analyticSubject$?: Subject<AnalyticObject> = new Subject<AnalyticObject>();
  public chatLocalErrorSubject$?: Subject<string> = new Subject<string>();

  // Explicit minimum level; when set it takes precedence over the legacy isDebug/disabled flags
  public logLevel?: ChatLogLevel;
  // When false, logs are only emitted via the subjects (host outputs) and never written to the console
  public consoleOutput: boolean = true;

  // Legacy flags: isDebug => 'debug', disabled => 'error'
  public isDebug: boolean = false;
  public disabled: boolean = false;

  constructor() {}

  public get effectiveLogLevel(): ChatLogLevel {
    // Ignore unrecognized values (e.g. 'ERROR' from an untyped host) so they fall back instead of silencing all logs
    if (this.logLevel && this.logLevel in LOG_LEVEL_RANK) {
      return this.logLevel;
    }
    if (this.disabled) {
      return 'error';
    }
    return this.isDebug ? 'debug' : 'info';
  }

  public isLevelEnabled(level: Exclude<ChatLogLevel, 'none'>): boolean {
    return LOG_LEVEL_RANK[level] >= LOG_LEVEL_RANK[this.effectiveLogLevel];
  }

  public debug(logEvent: ChatLogEvent) {
    this.emit('debug', logEvent);
  }

  public info(logEvent: ChatLogEvent) {
    this.emit('info', logEvent);
  }

  public warn(logEvent: ChatLogEvent) {
    this.emit('warn', logEvent);
  }

  public error(logEvent: ChatLogEvent) {
    // Always keep recent errors for the \cl-errors\ debug command, regardless of level
    if (this.recentErrors.length >= 10) {
      this.recentErrors.shift();
    }
    this.recentErrors.push(logEvent);
    this.emit('error', logEvent);
  }

  public messageError(event: ChatMessageErrorEvent) {
    this.error(event.exceptionLogEvent);
    if (event.errorMessage) {
      this.chatMessageErrorSubject$.next(event);
    }
  }

  public analyticEvent(analyticObject: AnalyticObject) {
    this.analyticSubject$.next(analyticObject);
  }

  public chatLocalError(errorMessage: string) {
    this.chatLocalErrorSubject$.next(errorMessage);
  }

  private emit(level: Exclude<ChatLogLevel, 'none'>, logEvent: ChatLogEvent) {
    if (!logEvent || !this.isLevelEnabled(level)) {
      return;
    }

    const event: ChatLogEvent = { ...logEvent, level };
    if (level === 'error') {
      this.errorSubject$.next(event);
    } else {
      this.infoSubject$.next(event);
    }

    if (this.consoleOutput) {
      const write = level === 'error' ? console.error : level === 'warn' ? console.warn : console.info;
      if (event.logParameters) {
        write(event.logMessage, ...event.logParameters);
      } else {
        write(event.logMessage);
      }
    }
  }
}
